import { createSelector } from "@reduxjs/toolkit";
import { ZodError, SafeParseReturnType, SafeParseError, z } from "zod";
import { selectCurrentProjectId } from "../../../global/globalSliceCommon";
import { REPORT_TEMPLATES } from "../../../mc-shared/reportAppConstants";
import { IReportKeyData, ReportCode } from "../../../mc-shared/reportSchemas/reportSchemas";
import { IRootState } from "../../../store";
import { reportRTK } from "./reportRTK";
import { getLatestVersionOfReport } from "./reportSlice";
import { deepEqual } from "../../utils";
import { IEksempelReportDocx, selectEksempelReportDocxData } from "./eksempelrapport/eksempelRapportSelectors";
import { flattenChaptersToObject } from "../../../components/ReportChapterTree/reportChaptersTreeUtils";
import { getBrannrapportDataToGenerateDocxWith } from "./brannrapport/Brannrapport";
import { IAkustikkPremissRapport } from "../../../mc-shared/reportSchemas/akustikk/akustikkPremissrapportSchema";

const selectCurrentReportId = (state: IRootState) => state.reportReducer.currentReportId;
export const selectHttpSingleReportBoard = createSelector(
  [(state: IRootState) => state, selectCurrentProjectId, selectCurrentReportId],
  (state, projectId, reportId) => {
    if (!projectId || !reportId) return null;
    const q = reportRTK.endpoints.getSingleReportBoard.select({ projectId, reportBoardId: reportId })(state);
    return q.data;
  },
);

export const selectReportData = createSelector([selectHttpSingleReportBoard], (reportBoard) => {
  if (!reportBoard) return null;
  return getLatestVersionOfReport(reportBoard)?.data || null;
});

export const selectEditData = (state: IRootState) => state.reportReducer.editedData;
export const selectEditedDataForCurrentReport = createSelector(
  [selectHttpSingleReportBoard, selectEditData],
  (reportBoard, editedData) => {
    if (!reportBoard) return null;
    const code = reportBoard.template as ReportCode;
    return editedData[code];
  },
);

export const selectIsEditedDataEqualToReportData = createSelector(
  [selectReportData, selectEditedDataForCurrentReport],
  (reportData, editedData) => {
    if (reportData == null && editedData == null) return true;
    if (reportData == null || editedData == null) return false;
    return deepEqual(reportData, editedData);
  },
);

export const selectCurrentReportCode = createSelector([selectHttpSingleReportBoard], (reportBoard) => {
  if (!reportBoard) return null;
  return reportBoard.template as ReportCode;
});

export const selectCurrentReportZodSchema = createSelector([selectCurrentReportCode], (code) => {
  if (!code) return null;

  const tpl = REPORT_TEMPLATES.find((t) => t.templateId === code);
  return tpl?.zodSchema ?? null; // zodSchema saknas för vissa (101/102)
});

export type ReportValidationResult = { ok: true; error: null } | { ok: false; error: ZodError };

const isParseError = (r: SafeParseReturnType<any, any>): r is SafeParseError<any> => r.success === false;

export const selectCurrentReportValidation = createSelector(
  [selectCurrentReportZodSchema, selectEditedDataForCurrentReport],
  (schema, editedData): ReportValidationResult => {
    if (!schema) return { ok: true, error: null };
    if (editedData == null) return { ok: true, error: null };

    // Säkra att TS ser detta som en Zod-schema-typ
    const zodSchema = schema as z.ZodTypeAny;

    const res = zodSchema.safeParse(editedData);

    if (!isParseError(res)) {
      return { ok: true, error: null };
    }

    return { ok: false, error: res.error };
  },
);

export const selectReportActivTemplateId = createSelector([selectHttpSingleReportBoard], (reportBoard) => {
  if (!reportBoard) return null;
  return reportBoard.template;
});

// export const selectDocxData = createSelector([selectReportActivTemplateId, selectEksempelReportDocxData], (reportData, ) => {

export const selectIsCurrentReportValid = createSelector([selectCurrentReportValidation], (v) => v.ok);

export type IReportDocxData = unknown; // eller en union om du vill (se längre ned)

export const selectCurrentReportDocxData = createSelector(
  [selectReportActivTemplateId, selectEditData],
  (code, editedData) => buildCurrentReportDocxData({ code: code as any, editedData }),
);

type Args = {
  code: ReportCode | null;
  editedData: IReportKeyData;
};

export const buildCurrentReportDocxData = ({ code, editedData }: Args): IReportDocxData | null => {
  if (!code) return null;

  const editedGeneric = editedData[code];

  switch (code) {
    case 101: {
      if (!editedGeneric) return null;

      // Här vet vi att det är eksempel
      const edited = editedGeneric as any; // eller IEksempelrapport om du vill importera den här
      const docx: IEksempelReportDocx = {
        ...edited,
        kapitlerFlat: flattenChaptersToObject(edited.kapitler),
      };

      return docx;
    }

    case 102: {
      if (!editedGeneric) return null;
      return getBrannrapportDataToGenerateDocxWith(editedGeneric as any);
    }

    case 401: {
      if (!editedGeneric) return null;

      const edited = editedGeneric as any; // eller IBrannrapport om du vill importera den här
      const docx: IAkustikkPremissRapport = {
        ...edited,
        kapitlerFlat: flattenChaptersToObject(edited.kapitler),
      };

      return docx;
    }

    default:
      // ingen konvertering
      return editedGeneric;
  }
};
